import { useState, useEffect } from "react";
import { ToggleLeft, ToggleRight, Zap, TrendingUp, Activity, Brain, Anchor, Droplets, BarChart3 } from "lucide-react";
import { API_BASE } from "@/lib/api";

interface Strategy {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  config: Record<string, any>;
}

export default function StrategiesPage() {
  const [strategies, setStrategies] = useState<Record<string, Strategy>>({});
  const [crewMode, setCrewMode] = useState("hybrid");
  const [loading, setLoading] = useState(false);

  const fetchStrategies = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/strategies/list`);
      const data = await r.json();
      setStrategies(data.strategies || {});
      setCrewMode(data.crew_mode || "hybrid");
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchStrategies();
  }, []);

  const toggleStrategy = async (key: string, enabled: boolean) => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/strategies/${key}/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !enabled }),
      });
      setStrategies((prev) => ({
        ...prev,
        [key]: { ...prev[key], enabled: !enabled },
      }));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const getIcon = (key: string) => {
    switch (key) {
      case "latency_arbitrage": return Zap;
      case "rare_event": return Brain;
      case "mean_reversion": return Activity;
      case "momentum": return TrendingUp;
      case "whale_copy": return Anchor;
      case "auto_lp": return Droplets;
      default: return BarChart3;
    }
  };

  const getColor = (key: string) => {
    switch (key) {
      case "latency_arbitrage": return "text-yellow-400";
      case "rare_event": return "text-purple-400";
      case "mean_reversion": return "text-blue-400";
      case "momentum": return "text-green-400";
      case "whale_copy": return "text-orange-400";
      case "auto_lp": return "text-cyan-400";
      default: return "text-gray-400";
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <BarChart3 size={18} className="text-terminal-accent" />
            STRATEGIES
          </h1>
          <p className="text-xs text-gray-500 mt-1">Toggle and configure all trading strategies</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">CREW MODE:</span>
          <span className="text-xs text-terminal-accent font-bold uppercase">{crewMode}</span>
        </div>
      </div>

      {/* Strategy Cards */}
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(strategies).map(([key, s]) => {
          const Icon = getIcon(key);
          const color = getColor(key);
          return (
            <div key={key} className="terminal-window p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 border border-terminal-border rounded ${color}`}>
                    <Icon size={18} />
                  </div>
                  <div>
                    <div className="text-sm text-white font-bold">{s.name}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{s.description}</div>
                  </div>
                </div>
                <button
                  onClick={() => toggleStrategy(key, s.enabled)}
                  disabled={loading}
                  className="text-terminal-accent"
                >
                  {s.enabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} className="text-gray-600" />}
                </button>
              </div>

              {/* Config display */}
              <div className="mt-3 border-t border-terminal-border pt-3">
                <div className="text-[10px] text-gray-500 mb-1">CONFIGURATION</div>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(s.config).map(([k, v]) => {
                    if (k === "enabled" || k === "markets") return null;
                    return (
                      <div key={k} className="bg-black/30 rounded px-2 py-1">
                        <div className="text-[9px] text-gray-500 uppercase">{k.replace(/_/g, " ")}</div>
                        <div className="text-xs text-white">{typeof v === "number" ? v.toFixed(2) : String(v)}</div>
                      </div>
                    );
                  })}
                  {s.config.markets && (
                    <div className="bg-black/30 rounded px-2 py-1 col-span-3">
                      <div className="text-[9px] text-gray-500 uppercase">Markets</div>
                      <div className="text-xs text-terminal-accent">{s.config.markets.join(", ")}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Strategy Descriptions */}
      <div className="terminal-window p-4">
        <div className="text-xs text-gray-500 mb-3">HOW STRATEGIES WORK</div>
        <div className="space-y-2 text-xs text-gray-300">
          <div className="flex items-start gap-2">
            <Zap size={12} className="text-yellow-400 mt-0.5" />
            <span><strong className="text-white">Latency Arbitrage:</strong> Monitors Binance price moves and trades Polymarket contracts before the market adjusts. Requires sub-second reaction.</span>
          </div>
          <div className="flex items-start gap-2">
            <Brain size={12} className="text-purple-400 mt-0.5" />
            <span><strong className="text-white">Rare Events:</strong> Uses Poisson distribution to find true probability in low-odds markets. Fat-tail adjusted (3.2x crypto, 2.1x politics).</span>
          </div>
          <div className="flex items-start gap-2">
            <Activity size={12} className="text-blue-400 mt-0.5" />
            <span><strong className="text-white">Mean Reversion:</strong> Z-score based entries when price deviates {'>'}2 sigma from lookback mean. Exits at 0.5 sigma.</span>
          </div>
          <div className="flex items-start gap-2">
            <TrendingUp size={12} className="text-green-400 mt-0.5" />
            <span><strong className="text-white">Momentum:</strong> Volume spike thresholds + short/long MA crossovers. Captures trending markets.</span>
          </div>
          <div className="flex items-start gap-2">
            <Droplets size={12} className="text-cyan-400 mt-0.5" />
            <span><strong className="text-white">Auto LP:</strong> Places two-sided maker orders in high-reward liquidity zones. Earns passive LP fees 24/7.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
